import { User } from '../types/User';
import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { UsersFilterMultipleValuesCount, UsersFilters } from '../types/filters';
import { useGetProtectedEntities, useGetSystemData, useGetUsers } from '../api';
import { Alert } from '@silverfort/sf-components';
import { SystemData } from '../types/SystemData';
import { Entity, useGetUserData, UserSession } from '../../../api';
import { MATCHING_STATUS_FILTER_DETAILS, STATUS_FILTER_DETAILS } from '../components/UsersTableFilters/consts';
import { filterAllValuesFilters } from '../utils';

type UserManagementContextType = {
    users: User[];
    hasMore?: boolean;
    totalUsersCount?: number;
    fetchMore: () => void;
    mutate: () => Promise<void>;
    loading: boolean;
    error: unknown;
    filters: UsersFilters;
    setFilters: Dispatch<SetStateAction<UsersFilters>>;
    systemData?: SystemData;
    loadingUsers: boolean;
    userSession?: UserSession;
    loadingUserSession: boolean;
    savedProtectedEntities: Entity[];
    fetchProtectedEntities: () => Promise<void>;
    loadingProtectedEntities: boolean;
    toolbarExtraContent: ReactNode;
    setToolbarExtraContent: (content: ReactNode) => void;
};

const UserManagementContext = createContext<UserManagementContextType | undefined>(undefined);

const MULTIPLE_FILTERS: UsersFilterMultipleValuesCount = {
    [STATUS_FILTER_DETAILS.id]: STATUS_FILTER_DETAILS.options.length,
    [MATCHING_STATUS_FILTER_DETAILS.id]: MATCHING_STATUS_FILTER_DETAILS.options.length
};

interface Props {
    children: ReactNode;
}

export const UserManagementProvider: FC<Props> = ({ children }) => {
    const [filters, setFilters] = useState<UsersFilters>({});
    const [apiFilters, setApiFilters] = useState<UsersFilters>({});
    const [toolbarExtraContent, setToolbarExtraContent] = useState<ReactNode>();
    const { userSession, loading: loadingUserSession } = useGetUserData();
    const { systemData, loading: loadingSystemData } = useGetSystemData();
    const {
        savedProtectedEntities = [],
        fetchData: fetchProtectedEntities,
        loading: loadingProtectedEntities
    } = useGetProtectedEntities();
    const {
        users = [],
        total: totalUsersCount,
        fetchData,
        loading: loadingUsers,
        ...restOfReturnValues
    } = useGetUsers({ filters: apiFilters });

    useEffect(() => {
        setApiFilters(filterAllValuesFilters(filters, MULTIPLE_FILTERS));
    }, [filters]);

    const contextValues = {
        ...restOfReturnValues,
        filters,
        setFilters,
        mutate: fetchData,
        users,
        loadingUsers,
        systemData,
        loading: loadingUsers || loadingSystemData,
        userSession,
        loadingUserSession,
        savedProtectedEntities,
        fetchProtectedEntities,
        loadingProtectedEntities,
        totalUsersCount,
        toolbarExtraContent,
        setToolbarExtraContent
    };

    return (
        <>
            <UserManagementContext.Provider value={contextValues}>{children}</UserManagementContext.Provider>
            <Alert />
        </>
    );
};

export const useUsers = (): UserManagementContextType => {
    const context = useContext(UserManagementContext);

    if (!context) {
        throw new Error('useUsers must be used within an UserManagementProvider');
    }

    return context;
};
